
import { useState, useEffect, useRef } from 'react';
import { cn } from '@/lib/utils';

const FAQSection = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(entry.target);
        }
      },
      { threshold: 0.1 }
    );
    
    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }
    
    return () => {
      if (sectionRef.current) {
        observer.disconnect();
      }
    };
  }, []);

  const faqs = [
    {
      q: "What is your typical project timeline?",
      a: "Most projects take 2-3 months from discovery to launch, depending on complexity."
    },
    {
      q: "Do you provide ongoing support?",
      a: "Yes, we offer various maintenance and support packages after launch."
    },
    {
      q: "What kind of business problems can you help solve?",
      a: "We help businesses of any size tackle challenges and drive growth using advanced technology and AI."
    },
    {
      q: "How do you handle project pricing?",
      a: "We offer both fixed-price and time-and-materials pricing models based on project requirements and scope."
    }
  ];

  return (
    <section ref={sectionRef} className="py-10 sm:py-12 md:py-16 px-4 sm:px-6 bg-white">
      <div className="max-w-4xl mx-auto">
        <h2 className={cn(
          "text-xl sm:text-2xl font-bold mb-6 sm:mb-8 text-center transition-all duration-700",
          isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
        )}>FAQ</h2>
        
        <div className="space-y-4 sm:space-y-6">
          {faqs.map((faq, index) => (
            <div 
              key={index} 
              className={cn(
                "p-4 sm:p-6 rounded-xl border border-border hover:border-primary/30 transition-all duration-700",
                isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
              )}
              style={{ transitionDelay: `${index * 100}ms` }}
            >
              <h3 className="text-lg sm:text-xl font-semibold mb-2">{faq.q}</h3>
              <p className="text-sm sm:text-base text-muted-foreground">{faq.a}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default FAQSection;
